import React from "react";
import { PlaidConnectionErrors } from "@common/constants";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { AlertIcon } from "@icons";
import styles from "./UnsupportedPlaidConnection.module.scss";

interface Props {
    error: PlaidConnectionErrors | null;
}

const NotSupportedTooltipContent: React.FC = () => (
    <VStack gap={8} className={styles.tooltipContent}>
        <p>
            Plaid can't support this account due to restrictions at your
            financial institution.
        </p>
        <div>
            <p>Possible causes:</p>
            <ul className={styles.causes}>
                <li>
                    the institution doesn't allow this account type to be
                    accessed by third parties
                </li>
                <li>
                    the institution uses an unsupported multi-factor
                    authentication method
                </li>
                <li>
                    the credentials belong to a guest or limited-access account
                </li>
            </ul>
        </div>
        <p>
            Reconnecting won't fix this. Contact Kick support if you need
            further assistance.
        </p>
    </VStack>
);

const RemovedTooltipContent: React.FC = () => (
    <VStack gap={8} className={styles.tooltipContent}>
        <p>Plaid removed this connection, so it can't sync.</p>
        <p>
            Reconnecting won't fix this. Connect the bank again to resume
            syncing.
        </p>
    </VStack>
);

export const UnsupportedPlaidConnectionTooltipContent: React.FC<Props> = ({
    error,
}) =>
    error === PlaidConnectionErrors.ITEM_REMOVED ? (
        <RemovedTooltipContent />
    ) : (
        <NotSupportedTooltipContent />
    );

export const UnsupportedPlaidConnection: React.FC<Props> = ({ error }) => (
    <Tooltip
        content={<UnsupportedPlaidConnectionTooltipContent error={error} />}
    >
        <HStack gap={4} align="center" className={styles.indicator}>
            <AlertIcon className="icon-color-red-500" />
            <span>
                {error === PlaidConnectionErrors.ITEM_REMOVED
                    ? "Connection removed"
                    : "Connection not supported"}
            </span>
        </HStack>
    </Tooltip>
);
